import { useSelector } from 'react-redux';

import { Icon, Tooltip } from '@components';
import CurrencyAmount from '@components/currency-amount/CurrencyAmount';
import { DEFAULT_CURRENCY_CONFIG } from '@hooks/useCurrency';

import theme from '../../../../../themes/colors';

interface AmountProgressBarProps {
  paidAmount: number;
  amount: number;
  isSplitPayment: boolean;
  isDisabled: boolean;
  id: string;
  showTooltip?: boolean;
}

const AmountProgressBar = ({
  paidAmount,
  amount,
  isSplitPayment,
  isDisabled,
  id,
  showTooltip = false
}: AmountProgressBarProps) => {
  const { currencyConfig } = useSelector((state: any) => state.user);

  const isDefaultCurrency =
    currencyConfig?.code === DEFAULT_CURRENCY_CONFIG.code;

  const pendingAmount: number =
    amount === 0 ? 0 : Math.round(100 - (paidAmount / amount) * 100);
  const styles = {
    progressWrapperStyle: {
      backgroundColor: theme.yellowLite4
    },
    progressBarStyle: {
      width: `${100 - pendingAmount}%`,
      backgroundColor: theme.progressBar
    }
  };

  return (
    <div className="flex items-center gap-2.5">
      <div
        style={styles.progressWrapperStyle}
        className="relative h-8.5 w-24 rounded-lg"
        data-tooltip-id={`partial-payment-amount-${id}`}
      >
        <div
          style={styles.progressBarStyle}
          className="flex h-full rounded-l-lg"
        />
        <CurrencyAmount
          value={amount}
          className={`absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center justify-center text-progressBarText ${
            isDefaultCurrency ? 'gap-0.25' : 'gap-0.5'
          }`}
          symbolClass={`${
            isDefaultCurrency ? 'text-[0.95em] !font-normal' : ''
          }`}
          valueClass=""
        />
      </div>
      {showTooltip && (
        <Tooltip
          id={`partial-payment-amount-${id}`}
          place="top"
          className="w-max max-w-500 shadow-tooltipShadow"
        >
          <div>Paid Amount</div>
          <div className="flex items-center text-sm">
            <CurrencyAmount
              value={paidAmount}
              className={`${isDefaultCurrency ? 'gap-0.25' : 'gap-0.5'}`}
              symbolClass={`${
                isDefaultCurrency ? 'text-[0.95em] !font-normal' : ''
              }`}
              valueClass=""
            />
          </div>
        </Tooltip>
      )}
      {isSplitPayment && (
        <>
          <Icon
            name="split-payment"
            className="size-4"
            size="none"
            stroke={isDisabled ? theme.grey40 : theme.yellow70}
            data-tooltip-id={`split-icon-${id}`}
          />
          <Tooltip
            id={`split-icon-${id}`}
            type="text"
            tooltipText="Partial Payment"
          />
        </>
      )}
    </div>
  );
};

export default AmountProgressBar;
